/**
 * Run background work only once the browser has nothing better to do.
 *
 * Warming the pages a producer hasn't opened yet must never compete with the
 * page they are actually looking at — `requestIdleCallback` is exactly that
 * promise. Safari still doesn't implement it, so fall back to a timeout that
 * clears the first paint.
 */

/** How long the browser may defer the task before it is run anyway. */
const IDLE_TIMEOUT_MS = 4000;
/** Where requestIdleCallback is missing (Safari): clear the first paint. */
const FALLBACK_DELAY_MS = 1500;

export interface IdleHandle {
  cancel: () => void;
}

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

export function scheduleIdle(task: () => void): IdleHandle {
  const w = typeof window === "undefined" ? undefined : (window as IdleWindow);

  if (w?.requestIdleCallback) {
    const handle = w.requestIdleCallback(task, { timeout: IDLE_TIMEOUT_MS });
    return { cancel: () => w.cancelIdleCallback?.(handle) };
  }

  const handle = setTimeout(task, FALLBACK_DELAY_MS);
  return { cancel: () => clearTimeout(handle) };
}
